import { Badge } from '@civilgrid/ui/Badge';
import { GridCellText } from '@civilgrid/ui/GridCellText';
import { Icon } from '@civilgrid/ui/Icon';
import { Label } from '@civilgrid/ui/Label';
import { Table, type TableColumn } from '@civilgrid/ui/Table';
import { cn } from '@civilgrid/ui/utils';
import { useParams } from '@tanstack/react-router';
import { useId, useState } from 'react';
import { getResearchStatusLabel } from '~/common/researchStatus';
import { useProjectIntelligence } from '~/hooks/useProjectIntelligence';
import { formatUTCDate } from '~/pages/homeViewV2/utils';

type DataRefreshStatus = {
  id: string;
  agencyName: string;
  type: string;
  lastRefreshed: string;
  refreshStatus: string;
};

export function DataRefreshStatus() {
  const headingId = useId();
  const { projectId } = useParams({ strict: false });

  const { data: projectIntelligence } = useProjectIntelligence({ projectId });

  const [expanded, setExpanded] = useState(false);

  const columns: TableColumn<DataRefreshStatus>[] = [
    {
      id: 'name',
      header: 'Jurisdiction',
      accessorKey: 'name',
      size: 180,
      cell: ({ row }) => {
        return <GridCellText variant="default">{row.original.agencyName}</GridCellText>;
      },
    },
    {
      id: 'refreshStatus',
      header: 'Refresh Status',
      accessorKey: 'refreshStatus',
      size: 150,
      cell: ({ row }) => {
        return <GridCellText variant="default">{getResearchStatusLabel(row.original.refreshStatus)}</GridCellText>;
      },
    },
    {
      id: 'lastRefreshed',
      header: 'Last Refresh',
      accessorKey: 'lastRefreshed',
      size: 120,
      cell: ({ row }) => {
        return (
          <GridCellText variant="default">
            {row.original.lastRefreshed === '' ? '—' : row.original.lastRefreshed}
          </GridCellText>
        );
      },
    },
    {
      id: 'type',
      header: 'Type',
      accessorKey: 'type',
      size: 175,
      cell: ({ row }) => {
        return <GridCellText variant="default">{row.original.type}</GridCellText>;
      },
    },
  ];

  const data: DataRefreshStatus[] =
    projectIntelligence?.conflictAgencies?.map((agency) => {
      return {
        id: agency.id,
        agencyName: agency.agencyName,
        type: agency.type,
        lastRefreshed: agency.lastRefresh ? formatUTCDate(agency.lastRefresh) : '',
        refreshStatus: agency.researchStatus ?? 'Not Started',
      };
    }) ?? [];

  const statusCount = data.filter((i) => i.refreshStatus === 'Published').length;

  return (
    <div className="pb-3">
      <div className="flex justify-between items-center mb-2">
        <button
          onClick={() => setExpanded(!expanded)}
          aria-expanded={expanded}
          data-testid="data-refresh-status-toggle"
          className="cursor-pointer flex items-center gap-1"
          type="button"
        >
          <Icon
            icon={'chevronDown'}
            className={cn('text-grey-60 transition-transform duration-300', !expanded ? '-rotate-90' : '')}
            size={14}
          />
          <Label id={headingId} className="text-sm font-medium cursor-pointer">
            Data Refresh Status
          </Label>
        </button>
        <Badge color={statusCount === 0 ? 'grey' : 'yellow'}>
          {statusCount === 0 ? 'No Updates' : `${statusCount} ${statusCount === 1 ? 'Update' : 'Updates'}`}
        </Badge>
      </div>
      {expanded && (
        <div>
          {data.length === 0 ? (
            <div className="text-center text-xs text-gray-500 py-2">No data refresh status found.</div>
          ) : (
            <Table
              aria-labelledby={headingId}
              columns={columns}
              data={data}
              enableColumnFilters={false}
              enableColumnOrdering={false}
              enableColumnPinning={false}
              enableColumnGrouping={false}
              enableHiding={false}
              maxHeight={200}
            />
          )}
        </div>
      )}
    </div>
  );
}
